/**
 * The family's week as a timetable rather than a list.
 *
 * The dashboard used to end in a list of active enrollments, which was the same list the
 * Students page already shows — program name, days, dates, one row each. Read down it, a
 * parent still could not answer the question they actually have on a Sunday evening: what does
 * Tuesday look like, and do the children overlap.
 *
 One collapse turns that data into something readable: **across days**. A programme meeting
 * Monday to Friday at the same hour is one bar five columns wide, not five boxes each too
 * narrow to hold a word of its own name. That is what BHCS scheduling actually looks like — a
 * long after-school block with hour-long enrichment classes running inside it on particular
 * days — so the run of days is the honest unit.
 *
 * Children are *not* collapsed. One row per child per programme: two siblings in the same
 * after-school block get a row each. It costs a little height and buys a great deal — every row
 * names exactly one child, every bar can link to that child's enrollment, and nothing has to be
 * inferred from a shared box.
 *
 * Everything here is a business-timezone calendar day and a wall clock — no instant is
 * constructed anywhere, because the one thing a schedule must never do is slide a Friday class
 * onto Thursday for a parent reading it from the east coast.
 *
 * This is inference, like `getNextSession` and unlike `buildTodaySessions`: it says when a
 * program is *scheduled* to meet, never that anyone attended. That is why the calendar never
 * renders attendance — what actually happened lives in the Today card, from records.
 *
 * It does skip days that will not happen: a closure for the calendar this program keeps, and
 * a plan-driven day the family did not buy. Both used to draw a bar, on the reasoning that a
 * timetable promises nothing — but the Today card on the same screen skips them, so the two
 * halves of one dashboard contradicted each other, and a Flex bar on an unbought day implies
 * a session nobody is paying for.
 *
 * A day a school closure empties says so. Skipping Labor Day left Monday a blank column,
 * which reads as a class gone missing rather than a day off. Only school-wide closures are
 * named: a vendor's own day off (`excluded_dates`) stays silent here, since it is one class
 * rather than the school, and the enrollment page already lists it.
 */

import { compactTimeRange, formatSessionTimeRange, getSessionWeekdays, sessionStartMinutes, shiftDateString, weekdayOfDateString } from './schedule';
import { programLocationName, type WithProgramLocation } from '../types/location';
import { formatDateDisplay } from './date';
import { closureLabelForProgramType, isClosedForProgramType, isDaySelectionBilling, isNonTeachingProgramType } from './programCalendar';
import { parentClosureLabel } from './sessionBreakdown';
import { DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../config/brands';

/**
 * The closure calendar a programme keeps. A missing or unrecognized brand reads as the
 * default, which is what the column's own DEFAULT would have given the row.
 */
function brandOf(program: { brand_key?: BrandKey | string | null }): BrandKey {
  return isBrandKey(program.brand_key) ? program.brand_key : DEFAULT_BRAND_KEY;
}

/** The slice of a program the timetable reads. */
export interface WeekProgram extends WithProgramLocation {
  name: string;
  type: string;
  schedule_cron: string;
  duration: number;
  start_date: string;
  end_date: string;
  /**
   * Which brand's closure calendar this programme keeps. Optional because rows selected
   * before the column existed omit it; absent reads as the default brand, matching the
   * column's own DEFAULT. Passing it is what stops a Kaleido class being cancelled by a BHCS
   * holiday.
   */
  brand_key?: BrandKey | null;
  /**
   * Load-bearing when present: for `per_day` and `flex_monthly` the cron lists the days a
   * family *may* choose, not the days they meet, so the plan is the only honest source.
   */
  billing_type?: string | null;
  /**
   * Days this programme alone does not meet, beyond the school-wide calendar — a vendor
   * taking its own day off. The teacher roster has honoured these since they existed; the
   * parent dashboard did not, so a family was offered a class its teacher knew was cancelled.
   */
  excluded_dates?: string[] | null;
}

/** An active enrollment, with enough of its student to label the bar. */
export interface WeekEnrollment {
  id: string;
  studentId: string;
  /** Full name — drives the initials chip's colour, which must match the child's avatar. */
  studentName: string;
  studentFirstName: string;
  programs: WeekProgram | null;
  /**
   * The days this family actually bought, for plan-driven billing types —
   * `metadata.selected_days` for `per_day`, the month's plan for `flex_monthly`.
   *
   * Required for those two, ignored for everything else. Missing means no day is confirmed,
   * which is a real answer rather than a gap: an unplanned Flex month holds no sessions.
   */
  selectedDays?: string[] | null;
}

/** The child a row belongs to. */
export interface WeekAttendee {
  studentId: string;
  name: string;
  firstName: string;
  enrollmentId: string;
}

export interface WeekDay {
  /** `YYYY-MM-DD`. */
  date: string;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
  isToday: boolean;
  /**
   * The school closure that emptied a day one of this family's programmes would have met —
   * "BHCS Closed — Labor Day", "Fall Camp". Absent on an ordinary day, and on a day off only
   * by a programme's own `excluded_dates`.
   */
  closure?: string;
}

/** A run of consecutive days a row's programme meets. */
export interface WeekBar {
  /** First day column covered (index into `days`). */
  dayStart: number;
  /** How many consecutive day columns it covers. */
  daySpan: number;
}

/**
 * One child's enrollment in one programme — a row of the timetable.
 *
 * A row, not a rectangle on an hour grid. An hour grid cannot hold both of the things this
 * calendar has to do at once: a bar spanning Monday to Friday is many columns wide, so
 * resolving a collision by nudging it into a sub-column moves it a fraction of *one* column
 * and leaves it sitting on top of its neighbour in all the others. Give each programme its own
 * row and the collision cannot arise — and the bar finally has the width to say its own name.
 * The hours are not lost, only moved: they run down the left edge, aligned, which is what a
 * timetable looks like anyway.
 */
export interface WeekRow {
  key: string;
  programName: string;
  /** Where it meets, or null when no location is on file. */
  locationName: string | null;
  programType: string;
  startMinutes: number;
  endMinutes: number;
  /** `9:00 – 3:00 PM`, or just the start when the program records no usable duration. */
  timeLabel: string;
  attendee: WeekAttendee;
  /** The day runs this programme meets. Monday/Wednesday/Friday is three bars, not one. */
  bars: WeekBar[];
}

/** An active enrollment that draws no box this week, and why. */
export interface OffWeekEnrollment {
  enrollmentId: string;
  programName: string;
  note: string;
}

export interface WeekSchedule {
  days: WeekDay[];
  /** Ordered by when they start, so the week reads down the page as the day runs. */
  rows: WeekRow[];
  offWeek: OffWeekEnrollment[];
}

const MINUTES_PER_DAY = 24 * 60;
const DAYS_IN_WEEK = 7;

/**
 * How long a session counts for when the program records no usable duration.
 *
 * Never shown: the row's label still prints only the start time, so nothing on screen asserts
 * an end that was never recorded. It exists so that two duration-less programmes at the same
 * hour still sort and group consistently.
 */
const UNKNOWN_DURATION_MINUTES = 30;

/** Sunday of the week containing `date`, as `YYYY-MM-DD`. */
export function startOfWeek(date: string): string {
  return shiftDateString(date, -weekdayOfDateString(date));
}

/** One child in one programme on one day, before the runs of days are joined up. */
interface DaySession {
  dayIndex: number;
  enrollmentId: string;
  programName: string;
  locationName: string | null;
  programType: string;
  startMinutes: number;
  endMinutes: number;
  timeLabel: string;
  attendee: WeekAttendee;
}

/**
 * The week's timetable: one row per child per programme, with each row's consecutive days
 * joined into bars.
 *
 * `today` is the business-timezone calendar day; the week runs Sunday to Saturday around it.
 */
/**
 * The seven dates of the week containing `today`, in order.
 *
 * Exported so a caller can fetch month-scoped data for exactly the week that will be drawn —
 * a week straddling a month boundary needs both months' Flex plans, and guessing from
 * `today` alone would miss one.
 */
export function weekDatesFor(today: string): string[] {
  const start = startOfWeek(today);
  return Array.from({ length: DAYS_IN_WEEK }, (_, i) => shiftDateString(start, i));
}

export function buildWeekSchedule(
  enrollments: readonly WeekEnrollment[],
  today: string,
): WeekSchedule {
  const weekStart = startOfWeek(today);
  const days: WeekDay[] = Array.from({ length: DAYS_IN_WEEK }, (_, i) => {
    const date = shiftDateString(weekStart, i);
    return { date, weekday: weekdayOfDateString(date), isToday: date === today };
  });
  const weekEnd = days[6].date;

  const sessions: DaySession[] = [];
  const offWeek: OffWeekEnrollment[] = [];

  for (const enrollment of enrollments) {
    const program = enrollment.programs;
    if (!program?.schedule_cron) continue;
    // A waitlist or registration fee is a real enrollment but never a session. Its cron is a
    // placeholder written to satisfy the column, so reading it as a timetable draws a class
    // seven days a week — and listing it as "also enrolled, not meeting" every week is noise
    // about something that was never going to meet.
    if (isNonTeachingProgramType(program.type)) continue;

    const startMinutes = sessionStartMinutes(program.schedule_cron);
    const weekdays = startMinutes === null ? new Set<number>() : getSessionWeekdays(program.schedule_cron);

    // A duration that would run past midnight is bad data rather than an overnight session,
    // and is treated the same as a missing one — matching `formatSessionTimeRange`, so the
    // box and its label can never disagree about whether an end time is known.
    const duration = Number(program.duration);
    const usable =
      startMinutes !== null &&
      Number.isFinite(duration) &&
      duration > 0 &&
      startMinutes + duration < MINUTES_PER_DAY;
    const span = usable ? duration : UNKNOWN_DURATION_MINUTES;

    let drew = false;
    /** School closures that emptied a day this programme would have met, each once. */
    const closedBy = new Set<string>();
    if (startMinutes !== null) {
      const endMinutes = startMinutes + span;
      const attendee: WeekAttendee = {
        studentId: enrollment.studentId,
        name: enrollment.studentName,
        firstName: enrollment.studentFirstName,
        enrollmentId: enrollment.id,
      };

      const timeLabel = compactTimeRange(formatSessionTimeRange(program.schedule_cron, program.duration) ?? '');
      const byPlan = isDaySelectionBilling(program.billing_type);
      /** Whether this enrollment has a session on a day its term and cron both allow. */
      const meets = (date: string): boolean => {
        // A family that picks its own days has already answered this; their answer wins over
        // the cron, which for them is only a menu.
        if (byPlan && !enrollment.selectedDays?.includes(date)) return false;
        // A vendor day off stands whatever the billing type: unlike a school closure, the
        // family's plan cannot outrank it, because the class genuinely is not running.
        if (program.excluded_dates?.includes(date)) return false;
        // Same calendar the roster and the Today card consult. Camps are exempt inside it.
        return byPlan || !isClosedForProgramType(program.type, date, brandOf(program));
      };
      days.forEach((day, dayIndex) => {
        if (day.date < program.start_date || day.date > program.end_date) return;
        if (!weekdays.has(day.weekday)) return;
        if (!meets(day.date)) {
          // Say why when the school calendar is the reason. Asked of the calendar this
          // programme keeps, so a no-enrichment day is named for enrichment and never for
          // after-school. A day off only by `excluded_dates` has no school label and stays
          // silent. A date holds one closure, so every programme it empties names it alike.
          const reason = parentClosureLabel(closureLabelForProgramType(program.type, day.date, brandOf(program)));
          if (reason) {
            day.closure = reason;
            closedBy.add(reason);
          }
          return;
        }
        drew = true;
        sessions.push({
          dayIndex,
          enrollmentId: enrollment.id,
          programName: program.name,
          locationName: programLocationName(program),
          programType: program.type,
          startMinutes,
          endMinutes,
          timeLabel,
          attendee,
        });
      });
    }

    if (!drew && !offWeek.some(o => o.programName === program.name)) {
      // Grouped by program for the same reason the blocks are: a camp three siblings are
      // booked into is one thing that is not happening this week, not three.
      offWeek.push({
        enrollmentId: enrollment.id,
        programName: program.name,
        note:
          program.start_date > weekEnd ? `starts ${formatDateDisplay(program.start_date)}`
          : program.end_date < weekStart ? `ended ${formatDateDisplay(program.end_date)}`
          // A week the school shut draws no row at all, so this line is the only place to say why.
          : closedBy.size > 0 ? [...closedBy].join(', ')
          : isDaySelectionBilling(program.billing_type) ? 'no days chosen this week'
          : 'no sessions this week',
      });
    }
  }

  return { ...trimQuietWeekend(days, toRows(sessions)), offWeek };
}

/**
 * Drop Saturday and Sunday when nothing is happening on them.
 *
 * Seven columns is a lot to ask of the space this grid has, and for most families two of them
 * are permanently blank — BHCS after-school runs on school days. A weekend column is kept only
 * when a programme actually meets on it. Being *today* is not enough: an empty Sunday column
 * is empty whether or not it happens to be Sunday, and the date range in the header already
 * says which week is on screen.
 *
 * Only the ends can be trimmed, and only when unused, so no bar can be left spanning a column
 * that no longer exists. Indices are rebased afterwards because `dayStart` indexes `days`.
 */
function trimQuietWeekend(days: WeekDay[], rows: WeekRow[]): { days: WeekDay[]; rows: WeekRow[] } {
  const used = new Set(rows.flatMap(r => r.bars.flatMap(b =>
    Array.from({ length: b.daySpan }, (_, i) => b.dayStart + i))));

  const keepSunday = used.has(0);
  const keepSaturday = used.has(6);
  if (keepSunday && keepSaturday) return { days, rows };

  const shift = keepSunday ? 0 : 1;
  return {
    days: days.slice(shift, keepSaturday ? 7 : 6),
    rows: rows.map(row => ({
      ...row,
      bars: row.bars.map(bar => ({ ...bar, dayStart: bar.dayStart - shift })),
    })),
  };
}

/**
 * Turn per-day sessions into one row per enrollment, with its days joined into runs.
 *
 * An enrollment is already exactly one child in one programme, so grouping needs no key beyond
 * its id — which is also why every row can carry a link, and why nothing has to be worked out
 * from a set of children sharing a box.
 *
 * Within a row, only *consecutive* days join into a bar. A Monday/Wednesday/Friday class draws
 * three bars on one row, because a single bar from Monday to Friday would claim Tuesday.
 */
function toRows(sessions: DaySession[]): WeekRow[] {
  const groups = new Map<string, DaySession[]>();
  for (const session of sessions) {
    const bucket = groups.get(session.enrollmentId);
    if (bucket) bucket.push(session);
    else groups.set(session.enrollmentId, [session]);
  }

  const rows: WeekRow[] = [];
  for (const [key, bucket] of groups) {
    bucket.sort((a, b) => a.dayIndex - b.dayIndex);

    const bars: WeekBar[] = [];
    for (const session of bucket) {
      const last = bars[bars.length - 1];
      if (last && session.dayIndex === last.dayStart + last.daySpan) last.daySpan++;
      else bars.push({ dayStart: session.dayIndex, daySpan: 1 });
    }

    const first = bucket[0];
    rows.push({
      key,
      programName: first.programName,
      locationName: first.locationName,
      programType: first.programType,
      startMinutes: first.startMinutes,
      endMinutes: first.endMinutes,
      timeLabel: first.timeLabel,
      attendee: first.attendee,
      bars,
    });
  }

  // Chronological, so the week reads top to bottom the way the day does. Programme then child
  // breaks ties, which puts two siblings in the same class on adjacent rows and makes the order
  // stable rather than dependent on which enrollment happened to load first.
  return rows.sort(
    (a, b) =>
      a.startMinutes - b.startMinutes ||
      a.endMinutes - b.endMinutes ||
      a.programName.localeCompare(b.programName) ||
      a.attendee.firstName.localeCompare(b.attendee.firstName),
  );
}
